import React, { useState, useEffect, useCallback } from 'react';
import { Users, Calendar, DollarSign, ListChecks, ClipboardList, CreditCard, TrendingUp, CheckCircle2, XCircle, RefreshCw, X, Send, Download, MessageCircle, AlertTriangle, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { AttendanceAdminOverview } from './AttendanceAdminOverview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ['receptionist', 'pharmacist', 'nurse', 'labtech'];
const DISPLAY_ROLES = [
  { key: 'nurse', label: 'Nurse' },
  { key: 'receptionist', label: 'Reception' },
  { key: 'labtech', label: 'Lab Tech' },
  { key: 'pharmacist', label: 'Pharmacist' },
];

const ROLE_BG = {
  nurse: { done: 'bg-green-500', partial: 'bg-red-400' },
  receptionist: { done: 'bg-green-500', partial: 'bg-red-400' },
  labtech: { done: 'bg-green-500', partial: 'bg-red-400' },
  pharmacist: { done: 'bg-green-500', partial: 'bg-red-400' },
};

const ManagementDashboard = ({ user }) => {
  const [overview, setOverview] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState({ role: 'receptionist', staff_name: '', date: new Date().toISOString().split('T')[0], task_description: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateReport, setDateReport] = useState(null);
  const [monthlyProgress, setMonthlyProgress] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [taskLogMonth, setTaskLogMonth] = useState(new Date().toISOString().slice(0, 7));
  const [taskLog, setTaskLog] = useState([]);
  const [showApptDetail, setShowApptDetail] = useState(false);
  const [pendingPopup, setPendingPopup] = useState(null); // {date, role, label, tasks}
  const [assignMode, setAssignMode] = useState('task'); // 'task' or 'defaults'
  const [defaultTasks, setDefaultTasks] = useState([]);
  const [editingDefaultRole, setEditingDefaultRole] = useState('receptionist');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, tasksRes] = await Promise.all([
        axios.get(`${API}/staff/management/overview`, { withCredentials: true }),
        axios.get(`${API}/staff/management/all-tasks`, { withCredentials: true }),
      ]);
      setOverview(overviewRes.data);
      setAllTasks(tasksRes.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  const fetchDateReport = useCallback(async (d) => {
    try {
      const { data } = await axios.get(`${API}/staff/management/date-report?date=${d}`, { withCredentials: true });
      setDateReport(data);
      setShowApptDetail(false);
    } catch { /* ignore */ }
  }, []);

  const fetchMonthlyProgress = useCallback(async (m) => {
    try {
      const { data } = await axios.get(`${API}/staff/management/monthly-progress?month=${m}`, { withCredentials: true });
      setMonthlyProgress(data);
    } catch { /* ignore */ }
  }, []);

  const fetchTaskLog = useCallback(async (m) => {
    try {
      const { data } = await axios.get(`${API}/staff/management/monthly-task-log?month=${m}`, { withCredentials: true });
      setTaskLog(data);
    } catch { /* ignore */ }
  }, []);

  const fetchDefaultTasks = useCallback(async (role) => {
    try {
      const { data } = await axios.get(`${API}/staff/default-tasks/${role}`, { withCredentials: true });
      setDefaultTasks(data.tasks || []);
    } catch { setDefaultTasks([]); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDateReport(selectedDate); }, [selectedDate, fetchDateReport]);
  useEffect(() => { fetchMonthlyProgress(selectedMonth); }, [selectedMonth, fetchMonthlyProgress]);
  useEffect(() => { fetchTaskLog(taskLogMonth); }, [taskLogMonth, fetchTaskLog]);

  const handleExportReport = async () => {
    try {
      toast.info('Generating report...');
      const response = await axios.get(`${API}/staff/export-daily-report`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const today = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute('download', `AMC_Daily_Report_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    } catch { toast.error('Export failed'); }
  };

  const handleShareWhatsApp = () => {
    if (!overview) return;
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const nl = '%0A';
    let apptSection = `*Appointments:* ${overview.appointments.total} (Today: ${overview.appointments.today})${nl}`;
    if (dateReport?.appointment_categories && Object.keys(dateReport.appointment_categories).length > 0) {
      apptSection += `  Category-wise (${selectedDate}):${nl}`;
      Object.entries(dateReport.appointment_categories).forEach(([cat, count]) => { apptSection += `  - ${cat}: ${count}${nl}`; });
    }
    let acctSection = `*Accounts:* ${overview.accounts.total} entries${nl}`;
    if (dateReport?.accounts?.length > 0) {
      dateReport.accounts.forEach(a => {
        const status = a.check ? 'OK' : 'MISMATCH';
        acctSection += `  ${a.time}: Box Rs.${(a.amt_in_box || 0).toLocaleString()} | Required Rs.${(a.required || 0).toLocaleString()} [${status}]${nl}`;
      });
    }
    const pendingCount = dateReport?.pending_wanted?.length || overview.wanted.pending_approval;
    let wantedSection = `*Wanted List:* ${overview.wanted.total} total, *${pendingCount} pending approval*${nl}`;
    if (dateReport?.pending_wanted?.length > 0) {
      dateReport.pending_wanted.slice(0, 5).forEach(w => { wantedSection += `  - ${w.item_details?.substring(0, 40)} (${w.category})${nl}`; });
      if (dateReport.pending_wanted.length > 5) wantedSection += `  ...and ${dateReport.pending_wanted.length - 5} more${nl}`;
    }
    const msg = `*AMC Daily Report - ${today}*${nl}${nl}${apptSection}${nl}${acctSection}${nl}${wantedSection}${nl}*Health Cards:* ${overview.healthcard.total} (${overview.healthcard.converted} converted)`;
    window.open(`https://wa.me/917010200869?text=${msg}`, '_blank');
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/staff/management/assign-task`, assignForm, { withCredentials: true });
      toast.success('Task assigned');
      setShowAssignForm(false);
      setAssignForm({ role: 'receptionist', staff_name: '', date: new Date().toISOString().split('T')[0], task_description: '' });
      setAssignMode('task');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign task'); }
  };

  const handleSaveDefaults = async () => {
    try {
      await axios.put(`${API}/staff/default-tasks/${editingDefaultRole}`, { tasks: defaultTasks }, { withCredentials: true });
      toast.success(`Default tasks updated for ${editingDefaultRole}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const openAssignForm = () => {
    setAssignMode('task');
    setShowAssignForm(true);
  };

  const openDefaultsEditor = async (role) => {
    setEditingDefaultRole(role || 'receptionist');
    await fetchDefaultTasks(role || 'receptionist');
    setAssignMode('defaults');
    setShowAssignForm(true);
  };

  const handleDefaultRoleChange = async (role) => {
    setEditingDefaultRole(role);
    await fetchDefaultTasks(role);
  };

  if (loading || !overview) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2540]"></div></div>;
  }

  const statCards = [
    { label: 'Total Staff', value: overview.total_staff, icon: Users, color: 'bg-purple-100 text-purple-700' },
    { label: 'Appointments', value: overview.appointments.total, sub: `${overview.appointments.today} today`, icon: Calendar, color: 'bg-blue-100 text-blue-700' },
    { label: 'Accounts', value: overview.accounts.total, sub: `${overview.accounts.today} today`, icon: DollarSign, color: 'bg-green-100 text-green-700' },
    { label: 'Wanted Items', value: overview.wanted.total, sub: `${overview.wanted.pending_approval} pending`, icon: ListChecks, color: 'bg-amber-100 text-amber-700' },
    { label: 'Health Cards', value: overview.healthcard.total, sub: `${overview.healthcard.converted} converted`, icon: CreditCard, color: 'bg-pink-100 text-pink-700' },
    { label: 'Tasks Today', value: overview.tasks.today, icon: ClipboardList, color: 'bg-teal-100 text-teal-700' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Management Dashboard</h2>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleExportReport} className="bg-green-600 hover:bg-green-700" data-testid="export-daily-report"><Download className="h-4 w-4 mr-2" /> Daily Report</Button>
          <Button onClick={handleShareWhatsApp} className="bg-[#25D366] hover:bg-[#1da851]" data-testid="share-whatsapp-report"><MessageCircle className="h-4 w-4 mr-2" /> Send to WhatsApp</Button>
          <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={openAssignForm} className="bg-[#0A2540]" data-testid="assign-task-btn"><Send className="h-4 w-4 mr-2" /> Assign Task</Button>
          <Button onClick={() => openDefaultsEditor('receptionist')} variant="outline" className="border-[#0A2540] text-[#0A2540]" data-testid="edit-defaults-btn"><ClipboardList className="h-4 w-4 mr-2" /> Edit Default Tasks</Button>
        </div>
      </div>

      <div className="flex gap-2 border-b pb-2 overflow-x-auto">
        {['overview', 'staff-tasks', 'incentives'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium capitalize whitespace-nowrap ${activeTab === tab ? 'bg-white text-[#0A2540] shadow-sm border-b-2 border-[#0A2540]' : 'text-[#475569] hover:bg-white/50'}`}
            data-testid={`mgmt-tab-${tab}`}>{tab.replace('-', ' ')}</button>
        ))}
      </div>

      {showAssignForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A2540]">{assignMode === 'defaults' ? 'Edit Default Tasks' : 'Assign Task to Staff'}</h2>
              <button onClick={() => { setShowAssignForm(false); setAssignMode('task'); }}><X className="h-5 w-5" /></button>
            </div>

            {/* Mode tabs */}
            <div className="flex gap-2 mb-4">
              <button onClick={() => setAssignMode('task')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${assignMode === 'task' ? 'bg-[#0A2540] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`} data-testid="assign-mode-task">Assign Task</button>
              <button onClick={() => { setAssignMode('defaults'); fetchDefaultTasks(editingDefaultRole); }} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${assignMode === 'defaults' ? 'bg-[#0A2540] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`} data-testid="assign-mode-defaults">Edit Default Tasks</button>
            </div>

            {assignMode === 'task' ? (
              <form onSubmit={handleAssignTask} className="space-y-3">
                <div><Label>Role</Label><select className="w-full border rounded-lg px-3 py-2 text-sm capitalize" value={assignForm.role} onChange={e => setAssignForm({ ...assignForm, role: e.target.value })} data-testid="assign-role">{ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}</select></div>
                <div><Label>Staff Name (optional)</Label><Input value={assignForm.staff_name} onChange={e => setAssignForm({ ...assignForm, staff_name: e.target.value })} placeholder="Leave blank for entire role" data-testid="assign-staff-name" /></div>
                <div><Label>Date</Label><Input type="date" value={assignForm.date} onChange={e => setAssignForm({ ...assignForm, date: e.target.value })} required data-testid="assign-date" /></div>
                <div><Label>Task Description</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} value={assignForm.task_description} onChange={e => setAssignForm({ ...assignForm, task_description: e.target.value })} required placeholder="Describe the task..." data-testid="assign-description" /></div>
                <Button type="submit" className="w-full bg-[#0A2540]" data-testid="assign-submit">Assign Task</Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Role</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm capitalize" value={editingDefaultRole} onChange={e => handleDefaultRoleChange(e.target.value)} data-testid="defaults-role-select">
                    {[...ROLES, 'admin'].map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Default Checklist ({defaultTasks.length} tasks)</Label>
                  {defaultTasks.map((task, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-[#F8FAFC] rounded-lg p-2">
                      <Input value={task.category} onChange={e => setDefaultTasks(prev => prev.map((t, i) => i === idx ? { ...t, category: e.target.value } : t))} placeholder="Category" className="w-28 text-xs" data-testid={`default-cat-${idx}`} />
                      <Input value={task.description} onChange={e => setDefaultTasks(prev => prev.map((t, i) => i === idx ? { ...t, description: e.target.value } : t))} placeholder="Task description" className="flex-1" data-testid={`default-desc-${idx}`} />
                      <button type="button" onClick={() => setDefaultTasks(prev => prev.filter((_, i) => i !== idx))} className="p-1 text-red-500 hover:bg-red-50 rounded"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setDefaultTasks(prev => [...prev, { category: 'Other', description: '' }])} className="text-sm text-[#0EA5E9] font-medium hover:underline" data-testid="add-default-task">+ Add Task</button>
                </div>
                <Button onClick={handleSaveDefaults} className="w-full bg-[#0A2540]" data-testid="save-defaults-btn">Save Default Tasks</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {statCards.map(card => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className={`inline-flex p-2 rounded-lg mb-2 ${card.color}`}><Icon className="h-4 w-4" /></div>
                  <p className="text-2xl font-bold text-[#0A2540]">{card.value}</p>
                  <p className="text-xs text-[#475569]">{card.label}</p>
                  {card.sub && <p className="text-xs text-[#94A3B8] mt-0.5">{card.sub}</p>}
                </div>
              );
            })}
          </div>

          <AttendanceAdminOverview data={overview.attendance} onUpdated={fetchData} />

          {/* Date Report Section */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold text-[#0A2540]">Date Report</h3>
              <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-auto" data-testid="dashboard-date-picker" />
            </div>
            {dateReport ? (
              <div className="space-y-4">
                {/* Account Summary */}
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2540] mb-2 flex items-center"><DollarSign className="h-4 w-4 mr-1 text-green-600" /> Account Status - {selectedDate}</h4>
                  {dateReport.accounts.length === 0 ? (
                    <p className="text-sm text-[#94A3B8]">No account entries for this date</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {dateReport.accounts.map(a => (
                        <div key={a.id} className={`rounded-xl p-4 border-2 ${a.check ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-[#0A2540]">{a.time} Shift</span>
                            {a.check ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-500" />}
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-[#475569]">Amt in Box</span><span className="font-bold">Rs.{(a.amt_in_box || 0).toLocaleString()}</span></div>
                            <div className="flex justify-between"><span className="text-[#475569]">Required</span><span className="font-bold">Rs.{(a.required || 0).toLocaleString()}</span></div>
                            <div className="flex justify-between"><span className="text-[#475569]">Turnover</span><span className="font-bold text-green-600">Rs.{(a.turnover || 0).toLocaleString()}</span></div>
                            <div className={`text-xs font-bold mt-1 px-2 py-1 rounded text-center ${a.check ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {a.check ? 'CHECK OK' : 'MISMATCH - Review Required'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Appointments - clickable */}
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2540] mb-2 flex items-center"><Calendar className="h-4 w-4 mr-1 text-blue-600" /> Appointments - {selectedDate}</h4>
                  {dateReport.appointments.length === 0 ? (
                    <p className="text-sm text-[#94A3B8]">No appointments for this date</p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-3 mb-2">
                        {Object.entries(dateReport.appointment_categories).map(([cat, count]) => (
                          <div key={cat} className="bg-blue-50 rounded-lg px-4 py-2 border border-blue-200">
                            <span className="text-sm font-medium text-[#0A2540]">{cat}</span>
                            <span className="ml-2 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">{count}</span>
                          </div>
                        ))}
                        <button
                          onClick={() => setShowApptDetail(!showApptDetail)}
                          className="bg-[#0A2540] rounded-lg px-4 py-2 text-white hover:bg-[#1E3A5F] transition-colors cursor-pointer"
                          data-testid="appt-total-toggle"
                        >
                          <span className="text-sm font-medium">Total</span>
                          <span className="ml-2 text-lg font-bold">{dateReport.appointments.length}</span>
                          <span className="ml-2 text-xs">{showApptDetail ? '(hide)' : '(click to view)'}</span>
                        </button>
                      </div>
                      {showApptDetail && (
                        <div className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0] max-h-60 overflow-y-auto" data-testid="appt-detail-list">
                          <table className="w-full text-sm">
                            <thead><tr className="border-b">
                              <th className="text-left py-1 pr-3 font-semibold text-[#0A2540]">Patient</th>
                              <th className="text-left py-1 pr-3 font-semibold text-[#0A2540]">Phone</th>
                              <th className="text-left py-1 pr-3 font-semibold text-[#0A2540]">For</th>
                              <th className="text-left py-1 font-semibold text-[#0A2540]">Status</th>
                            </tr></thead>
                            <tbody>
                              {dateReport.appointments.map(a => (
                                <tr key={a.id} className="border-b border-[#E2E8F0] last:border-0">
                                  <td className="py-2 pr-3 font-medium text-[#0A2540]">{a.patient_name}</td>
                                  <td className="py-2 pr-3"><a href={`tel:${a.mobile_no}`} className="text-[#0EA5E9] hover:underline flex items-center"><Phone className="h-3 w-3 mr-1" />{a.mobile_no}</a></td>
                                  <td className="py-2 pr-3 text-[#475569]">{a.appointment_for}</td>
                                  <td className="py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-[#F1F5F9]">{a.lead_status}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Pending Management Approvals */}
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2540] mb-2 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1 text-amber-500" /> Pending Management Approvals ({dateReport.pending_wanted.length})
                  </h4>
                  {dateReport.pending_wanted.length === 0 ? (
                    <p className="text-sm text-[#94A3B8]">No pending approvals</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {dateReport.pending_wanted.map(w => (
                        <div key={w.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                          <div><span className="text-sm font-medium text-[#0A2540]">{w.item_details}</span><span className="text-xs text-[#94A3B8] ml-2">{w.category} | {w.date}</span></div>
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Pending</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#94A3B8]">Select a date to view the report</p>
            )}
          </div>

          {/* Appointment Status Breakdown */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-[#0A2540] mb-3">Appointment Status (All Time)</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(overview.appointments.by_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2 bg-[#F1F5F9] rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-[#0A2540]">{status}</span>
                  <span className="bg-[#0A2540] text-white text-xs px-2 py-0.5 rounded-full">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Task Log Table */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold text-[#0A2540]">Monthly Task Log</h3>
              <Input type="month" value={taskLogMonth} onChange={e => setTaskLogMonth(e.target.value)} className="w-auto" data-testid="task-log-month-picker" />
            </div>
            {taskLog.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">No task data for {taskLogMonth}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="monthly-task-log-table">
                  <thead className="bg-[#F1F5F9]">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-[#0A2540] whitespace-nowrap">Date</th>
                      {DISPLAY_ROLES.map(r => (
                        <th key={r.key} className="px-3 py-2 text-center font-semibold text-[#0A2540] whitespace-nowrap">{r.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {taskLog.map(row => (
                      <tr key={row.date} className="hover:bg-[#F8FAFC]">
                        <td className="px-3 py-2 font-medium text-[#0A2540] whitespace-nowrap">{row.date}</td>
                        {DISPLAY_ROLES.map(r => {
                          const data = row.roles?.[r.key];
                          if (!data) return <td key={r.key} className="px-3 py-2 text-center text-[#CBD5E1]">-</td>;
                          const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
                          const barColor = pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
                          const hasPending = data.pending_tasks && data.pending_tasks.length > 0;
                          return (
                            <td key={r.key} className="px-3 py-2">
                              <button
                                className={`flex items-center gap-2 justify-center w-full ${hasPending ? 'cursor-pointer hover:opacity-80' : ''}`}
                                onClick={() => hasPending && setPendingPopup({ date: row.date, role: r.key, label: r.label, tasks: data.pending_tasks })}
                                data-testid={`tasklog-cell-${row.date}-${r.key}`}
                              >
                                <div className="w-16 bg-[#E2E8F0] rounded-full h-3 relative overflow-hidden">
                                  <div className={`h-3 rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className={`text-xs font-bold whitespace-nowrap ${pct === 100 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                                  {data.completed}/{data.total}
                                </span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pending Tasks Popup */}
          {pendingPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0A2540]">
                    Pending Tasks - {pendingPopup.label}
                  </h2>
                  <button onClick={() => setPendingPopup(null)}><X className="h-5 w-5" /></button>
                </div>
                <p className="text-sm text-[#475569] mb-3">{pendingPopup.date}</p>
                <div className="space-y-2" data-testid="pending-tasks-list">
                  {pendingPopup.tasks.map((task, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <XCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#0A2540]">{task}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8] mt-3">{pendingPopup.tasks.length} pending task(s)</p>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'staff-tasks' && (
        <div className="space-y-4">
          {/* Monthly Progress Bars */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold text-[#0A2540]">Staff Monthly Progress</h3>
              <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-auto" data-testid="monthly-progress-picker" />
            </div>
            {monthlyProgress.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">No task data for {selectedMonth}</p>
            ) : (
              <div className="space-y-3">
                {monthlyProgress.sort((a, b) => {
                  const pctA = a.total > 0 ? a.completed / a.total : 0;
                  const pctB = b.total > 0 ? b.completed / b.total : 0;
                  return pctB - pctA;
                }).map(p => {
                  const pct = p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0;
                  return (
                    <div key={`${p.name}-${p.role}`} className="bg-[#F8FAFC] rounded-xl p-4" data-testid={`monthly-progress-${p.role}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#0A2540]">{p.name}</span>
                          <span className="text-xs text-[#94A3B8] capitalize">({p.role})</span>
                        </div>
                        <span className="text-sm font-bold" style={{ color: pct === 100 ? '#22c55e' : pct >= 70 ? '#3b82f6' : pct >= 40 ? '#f59e0b' : '#ef4444' }}>
                          {pct}% ({p.completed}/{p.total})
                        </span>
                      </div>
                      <div className="w-full bg-[#E2E8F0] rounded-full h-3">
                        <div className={`h-3 rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct >= 70 ? 'bg-blue-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-[#0A2540]">Recent Task Entries</h3>
            {allTasks.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-[#94A3B8]">No tasks logged yet</div>
            ) : allTasks.slice(0, 20).map(entry => {
              const done = entry.summary?.completed || 0;
              const total = done + (entry.summary?.pending || 0);
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <div key={entry.id} className="bg-white rounded-xl shadow-sm p-4" data-testid={`mgmt-task-${entry.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[#0A2540]">{entry.date}</span>
                      <span className="text-xs bg-[#F1F5F9] px-2 py-1 rounded capitalize">{entry.role}</span>
                      <span className="text-xs text-[#475569]">{entry.staff_name}</span>
                      {entry.is_management_assigned && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Assigned</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-[#E2E8F0] rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-[#475569]">{done}/{total}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(entry.tasks || []).map((t, i) => (
                      <span key={`t-${i}`} className={`text-xs px-2 py-0.5 rounded ${t.completed ? 'bg-green-100 text-green-700 line-through' : 'bg-red-50 text-red-600'}`}>{t.description?.substring(0, 40)}</span>
                    ))}
                    {(entry.extra_tasks || []).filter(t => t.description).map((t, i) => (
                      <span key={`e-${i}`} className={`text-xs px-2 py-0.5 rounded ${t.completed ? 'bg-green-100 text-green-700 line-through' : 'bg-amber-50 text-amber-700'}`}>{t.description?.substring(0, 40)}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'incentives' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-[#0A2540] mb-3 flex items-center"><ClipboardList className="h-4 w-4 mr-2 text-blue-600" /> Task Completion ({selectedMonth})</h3>
            {monthlyProgress.length === 0 ? (
              <p className="text-[#94A3B8] text-sm">No task data</p>
            ) : (
              <div className="space-y-2">
                {monthlyProgress.sort((a, b) => b.completed - a.completed).map(p => {
                  const pct = p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0;
                  return (
                    <div key={`${p.name}-${p.role}`} className="flex items-center justify-between bg-[#F1F5F9] rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2"><span className="font-medium text-[#0A2540]">{p.name}</span><span className="text-xs text-[#94A3B8] capitalize">({p.role})</span></div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-[#475569]">{p.completed}/{p.total} tasks</span>
                        <span className="font-bold" style={{ color: pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444' }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-[#0A2540] mb-3 flex items-center"><CreditCard className="h-4 w-4 mr-2 text-pink-600" /> Health Card Incentives (Rs.20 per conversion)</h3>
            {overview.healthcard.incentives.length === 0 ? (
              <p className="text-[#94A3B8] text-sm">No conversions yet</p>
            ) : (
              <div className="space-y-2">
                {overview.healthcard.incentives.map(item => (
                  <div key={item._id} className="flex items-center justify-between bg-[#F1F5F9] rounded-lg px-4 py-3">
                    <div><span className="font-medium text-[#0A2540]">{item._id}</span><span className="text-xs text-[#475569] ml-2">{item.count} conversions</span></div>
                    <span className="font-bold text-green-600">Rs.{item.incentive}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementDashboard;
